import React, { useEffect, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, Tag, Check } from "@phosphor-icons/react";

const SWATCHES = [
  "#CFAB7A", // Bahama beige (default)
  "#1E8E5E", // forest green — brokers
  "#2E7D32", // active green
  "#0D6EFD", // route blue
  "#6F42C1", // franchise purple
  "#F57C00", // alert orange
  "#D32F2F", // hot red
  "#5C738A", // neutral slate
];

/**
 * Compact pill badge for a customer group. Used inside customer rows and
 * inside the group multi-select. The text color is computed against the
 * background luminance so light colors get a navy foreground.
 */
export function GroupBadge({ group, onRemove, size = "sm", testid }) {
  if (!group) return null;
  const bg = group.color || "#CFAB7A";
  const dark = isColorDark(bg);
  const fg = dark ? "#FFFFFF" : "#012340";
  const padX = size === "xs" ? "px-1.5" : "px-2";
  const padY = size === "xs" ? "py-0.5" : "py-0.5";
  const textSize = size === "xs" ? "text-[10px]" : "text-xs";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm ${padX} ${padY} ${textSize} font-bold tracking-wide`}
      style={{ background: bg, color: fg }}
      data-testid={testid || `group-badge-${group.id}`}
      title={group.name}
    >
      <Tag size={size === "xs" ? 10 : 11} weight="bold" />
      <span className="truncate max-w-[120px]">{group.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(group.id); }}
          className="ml-0.5 hover:opacity-70"
          aria-label={`Remove ${group.name}`}
          data-testid={`group-badge-remove-${group.id}`}
        >
          <X size={10} weight="bold" />
        </button>
      )}
    </span>
  );
}

function isColorDark(hex) {
  try {
    const c = hex.replace("#", "");
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    // ITU-R BT.709 luminance — matches how a sighted user perceives contrast.
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return lum < 0.55;
  } catch {
    return true;
  }
}

/**
 * Multi-select control used inside the Edit Customer modal so admins can
 * assign / unassign a customer to any number of groups. Reuses GroupBadge.
 */
export function GroupMultiSelect({ groups, selectedIds, onChange, onManage, testid = "group-multi" }) {
  const [open, setOpen] = useState(false);
  const selectedSet = new Set(selectedIds || []);
  const selected = (groups || []).filter((g) => selectedSet.has(g.id));

  function toggle(gid) {
    const next = new Set(selectedSet);
    if (next.has(gid)) next.delete(gid);
    else next.add(gid);
    onChange(Array.from(next));
  }

  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[42px] flex flex-wrap items-center gap-1.5 rounded-sm border border-[#012340]/20 bg-white px-3 py-2 text-left text-sm hover:border-[#CFAB7A] focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
        data-testid={`${testid}-toggle`}
      >
        {selected.length === 0 ? (
          <span className="text-[#5C738A]">No groups assigned</span>
        ) : (
          selected.map((g) => (
            <GroupBadge
              key={g.id}
              group={g}
              onRemove={(gid) => toggle(gid)}
            />
          ))
        )}
        <span className="ml-auto text-[#5C738A] text-xs">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-[#012340]/20 rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {(!groups || groups.length === 0) ? (
            <div className="px-3 py-4 text-sm text-[#5C738A] text-center">
              No groups yet.
              {onManage && (
                <button type="button" onClick={() => { setOpen(false); onManage(); }} className="block mx-auto mt-2 text-[#012340] font-bold underline">
                  Create your first group
                </button>
              )}
            </div>
          ) : (
            <>
              {groups.map((g) => {
                const checked = selectedSet.has(g.id);
                return (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => toggle(g.id)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#FAFAFA] ${checked ? "bg-[#CFAB7A]/10" : ""}`}
                    data-testid={`${testid}-option-${g.id}`}
                  >
                    <span
                      className={`w-4 h-4 rounded-sm border flex items-center justify-center ${checked ? "border-[#012340]" : "border-[#012340]/30"}`}
                      style={{ background: checked ? g.color : "transparent" }}
                    >
                      {checked && <Check size={10} weight="bold" className={isColorDark(g.color) ? "text-white" : "text-[#012340]"} />}
                    </span>
                    <span className="font-semibold text-sm text-[#012340] flex-1 truncate">{g.name}</span>
                    <span className="text-[10px] text-[#5C738A] font-mono">{g.customer_count}</span>
                  </button>
                );
              })}
              {onManage && (
                <button type="button" onClick={() => { setOpen(false); onManage(); }} className="w-full px-3 py-2 text-left text-xs font-bold text-[#012340] border-t border-[#012340]/10 hover:bg-[#FAFAFA]">
                  <Plus size={12} weight="bold" className="inline mr-1" /> Manage groups…
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Full-page modal for creating / renaming / deleting groups. Lives outside
 * the customer form so admins can also open it from the page-level filter bar.
 */
export default function CustomerGroupsManager({ open, onClose, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = create mode, group obj = edit mode
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState(SWATCHES[0]);
  const [formDesc, setFormDesc] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-groups");
      setGroups(data || []);
    } catch (e) {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      load();
      resetForm();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetForm() {
    setEditing(null);
    setFormName("");
    setFormColor(SWATCHES[0]);
    setFormDesc("");
  }

  function openEdit(g) {
    setEditing(g);
    setFormName(g.name);
    setFormColor(g.color || SWATCHES[0]);
    setFormDesc(g.description || "");
  }

  async function submit(e) {
    e.preventDefault();
    const name = formName.trim();
    if (!name) {
      toast.error("Group name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { name, color: formColor, description: formDesc.trim() };
      if (editing) {
        await api.put(`/customer-groups/${editing.id}`, payload);
        toast.success(`Group "${name}" updated`);
      } else {
        await api.post("/customer-groups", payload);
        toast.success(`Group "${name}" created`);
      }
      resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function del(g) {
    const msg = g.customer_count > 0
      ? `Delete "${g.name}"? ${g.customer_count} customer${g.customer_count === 1 ? "" : "s"} will be detached from this group. Customer records will NOT be deleted.`
      : `Delete "${g.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/customer-groups/${g.id}`);
      toast.success(`Group "${g.name}" deleted`);
      if (editing?.id === g.id) resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="customer-groups-manager">
      <div onClick={(e) => e.stopPropagation()} className="bg-white text-[#012340] w-full max-w-2xl rounded-sm shadow-xl max-h-[90vh] flex flex-col">
        <header className="px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C738A] font-bold">Customers</div>
            <h2 className="font-heading text-xl font-black tracking-tight">Manage Groups & Bundles</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-sm hover:bg-[#012340]/5" aria-label="Close" data-testid="groups-manager-close">
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="grid sm:grid-cols-2 gap-0 flex-1 min-h-0">
          {/* LEFT — list */}
          <div className="border-r border-[#012340]/10 overflow-y-auto p-4 space-y-2" data-testid="groups-manager-list">
            {loading && <div className="text-sm text-[#5C738A]">Loading groups…</div>}
            {!loading && groups.length === 0 && (
              <div className="text-sm text-[#5C738A] py-6 text-center">No groups yet. Create your first one →</div>
            )}
            {groups.map((g) => {
              const isEditing = editing?.id === g.id;
              return (
                <div
                  key={g.id}
                  className={`rounded-sm border px-3 py-2 ${isEditing ? "border-[#CFAB7A] bg-[#CFAB7A]/10" : "border-[#012340]/10 bg-white hover:border-[#012340]/30"}`}
                  data-testid={`groups-manager-row-${g.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: g.color }} />
                        <div className="font-bold text-sm truncate">{g.name}</div>
                      </div>
                      <div className="text-[11px] text-[#5C738A] mt-0.5 font-mono">
                        {g.customer_count} customer{g.customer_count === 1 ? "" : "s"}
                      </div>
                      {g.description && (
                        <div className="text-[11px] text-[#5C738A] mt-0.5 truncate">{g.description}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => openEdit(g)} className="p-1.5 rounded-sm hover:bg-[#012340]/5" title="Rename" data-testid={`groups-manager-edit-${g.id}`}>
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button type="button" onClick={() => del(g)} className="p-1.5 rounded-sm hover:bg-[#D32F2F]/10 text-[#D32F2F]" title="Delete" data-testid={`groups-manager-delete-${g.id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT — create / edit form */}
          <form onSubmit={submit} className="p-4 overflow-y-auto" data-testid="groups-manager-form">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C738A] font-bold">
              {editing ? "Edit group" : "New group"}
            </div>
            <h3 className="font-heading text-lg font-black tracking-tight mt-1 mb-3">
              {editing ? editing.name : "Create a group"}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-1">Name</label>
                <input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. BP Environmental"
                  className="w-full rounded-sm border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
                  data-testid="groups-manager-name-input"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-1">Color</label>
                <div className="flex flex-wrap gap-1.5">
                  {SWATCHES.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setFormColor(c)}
                      className={`w-7 h-7 rounded-sm border-2 ${formColor === c ? "border-[#012340]" : "border-transparent"}`}
                      style={{ background: c }}
                      aria-label={`Pick color ${c}`}
                      data-testid={`groups-manager-color-${c.replace("#", "")}`}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-1">Description (optional)</label>
                <input
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  placeholder="e.g. Brokered through Mike at BP Env"
                  className="w-full rounded-sm border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
                  data-testid="groups-manager-desc-input"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-1">Preview</label>
                <GroupBadge group={{ id: "preview", name: formName || "Group name", color: formColor }} testid="groups-manager-preview" />
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                type="submit"
                disabled={saving || !formName.trim()}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="groups-manager-save"
              >
                {saving ? "Saving…" : (editing ? "Save changes" : "Create group")}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-3 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold text-[#012340]"
                  data-testid="groups-manager-cancel-edit"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
